import { FormEvent, ReactNode, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { completeInitialChecklist } from '@/services/patients';
import { useAuth } from '@/hooks/useAuth';
import {
  BLOOD_TYPE_SELECT_OPTIONS,
  buildInitialChecklistPayload,
  initialPatientChecklistDefaults,
  initialPatientChecklistSteps,
  type InitialPatientChecklistAnswers,
} from '@/lib/initialPatientChecklist';
import { ConditionalTextarea } from '@/components/ui/conditional-textarea';
import { FamilyDiseaseHistoryFields } from '@/components/onboarding/FamilyDiseaseHistoryFields';
import {
  validateFamilyDiseaseHistory,
  type FamilyDiseaseHistory,
} from '@/lib/familyDiseaseHistory';

type FieldName = keyof InitialPatientChecklistAnswers;

const CONDITIONAL_CLINICAL_FIELDS: readonly FieldName[] = [
  'allergies',
  'medications',
  'preExistingConditions',
  'previousSurgeries',
  'lifestyleHabits',
] as const;

// ─── Máscaras de input ────────────────────────────────────────────────────────

/** Formata telefone: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX */
function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    // (XX) XXXX-XXXX
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  // (XX) XXXXX-XXXX
  return digits
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
}

/** Formata CPF: XXX.XXX.XXX-XX */
function maskCPF(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3}\.\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3}\.\d{3}\.\d{3})(\d)/, '$1-$2');
}

/** Formata CEP: XXXXX-XXX */
function maskCEP(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/^(\d{5})(\d)/, '$1-$2');
}

// Data máxima para data de nascimento (hoje)
const todayISO = new Date().toISOString().split('T')[0];

const fieldLabels: Record<FieldName, string> = {
  phone: 'Telefone',
  cpf: 'CPF',
  dateOfBirth: 'Data de nascimento',
  gender: 'Sexo',
  isBrazilian: 'É brasileiro?',
  country: 'País de origem',
  street: 'Rua',
  number: 'Número',
  complement: 'Complemento',
  neighborhood: 'Bairro',
  city: 'Cidade',
  state: 'Estado (UF)',
  zipCode: 'CEP',
  allergies: 'Alergias',
  medications: 'Medicamentos de uso contínuo',
  preExistingConditions: 'Doenças pré-existentes',
  familyHistory: 'Histórico de doenças na família',
  previousSurgeries: 'Cirurgias anteriores',
  lifestyleHabits: 'Hábitos de vida',
  bloodType: 'Tipo sanguíneo',
  height: 'Altura (m)',
  weight: 'Peso (kg)',
};

export default function InitialChecklistPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<InitialPatientChecklistAnswers>(
    initialPatientChecklistDefaults,
  );
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const numberInputRef = useRef<HTMLInputElement>(null);

  const currentStep = initialPatientChecklistSteps[stepIndex];
  const progress = Math.round(((stepIndex + 1) / initialPatientChecklistSteps.length) * 100);
  const isLastStep = stepIndex === initialPatientChecklistSteps.length - 1;

  const currentRequiredFields = useMemo(
    () => currentStep.requiredFields as readonly FieldName[],
    [currentStep],
  );

  const updateAnswer = (field: FieldName, value: string) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleCepChange = async (value: string) => {
    const maskedCep = maskCEP(value);
    updateAnswer('zipCode', maskedCep);

    const rawCep = maskedCep.replace(/\D/g, '');
    if (rawCep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${rawCep}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setAnswers((prev) => ({
            ...prev,
            street: data.logradouro || prev.street,
            neighborhood: data.bairro || prev.neighborhood,
            city: data.localidade || prev.city,
            state: data.uf || prev.state,
          }));
          setFieldErrors((prev) => ({
            ...prev,
            street: undefined,
            neighborhood: undefined,
            city: undefined,
            state: undefined,
          }));
          if (numberInputRef.current) {
            numberInputRef.current.focus();
          }
        } else {
          toast.error('CEP não encontrado.');
        }
      } catch (error) {
        toast.error('Erro ao buscar o CEP.');
      }
    }
  };

  const validateStep = () => {
    const nextErrors: Partial<Record<FieldName, string>> = {};

    currentRequiredFields.forEach((field) => {
      const val = answers[field];

      if (field === 'familyHistory') {
        const familyError = validateFamilyDiseaseHistory(answers.familyHistory);
        if (familyError) {
          nextErrors.familyHistory = familyError;
        }
        return;
      }

      if (CONDITIONAL_CLINICAL_FIELDS.includes(field)) {
        const text = typeof val === 'string' ? val.trim() : '';
        // “Não tenho” grava "Nenhum". “Tenho” exige texto real (vazio = incompleto).
        if (!text) {
          nextErrors[field] =
            `Informe ${fieldLabels[field].toLowerCase()} ou selecione “Não tenho/Nenhum”.`;
        }
        return;
      }

      if (typeof val === 'string') {
        if (!val.trim()) {
          nextErrors[field] = `${fieldLabels[field]} é obrigatório.`;
        }
      } else if (val === undefined || val === null) {
        nextErrors[field] = `${fieldLabels[field]} é obrigatório.`;
      }
    });

    if (currentStep.id === 'personal' && answers.isBrazilian === false && !answers.country.trim()) {
      nextErrors.country = 'Informe o país de origem.';
    }

    if (currentRequiredFields.includes('state') && answers.state.trim().length !== 2) {
      nextErrors.state = 'Informe a UF com 2 letras.';
    }

    if (currentRequiredFields.includes('cpf') && answers.cpf.replace(/\D/g, '').length !== 11) {
      nextErrors.cpf = 'Informe um CPF válido com 11 dígitos.';
    }

    if (currentRequiredFields.includes('dateOfBirth') && answers.dateOfBirth) {
      const selectedDate = new Date(answers.dateOfBirth);
      const today = new Date();
      // Reseta as horas de hoje para comparar apenas a data
      today.setHours(0, 0, 0, 0);

      if (isNaN(selectedDate.getTime())) {
        nextErrors.dateOfBirth = 'Data de nascimento inválida.';
      } else if (selectedDate > today) {
        nextErrors.dateOfBirth = 'A data de nascimento não pode ser no futuro.';
      }
    }

    if (currentRequiredFields.includes('height') && answers.height) {
      const h = parseFloat(answers.height);
      if (isNaN(h) || h <= 0 || h > 3) {
        nextErrors.height = 'Insira uma altura válida (ex: 1.75).';
      }
    }

    if (currentRequiredFields.includes('weight') && answers.weight) {
      const w = parseFloat(answers.weight);
      if (isNaN(w) || w <= 0 || w > 500) {
        nextErrors.weight = 'Insira um peso válido (ex: 70).';
      }
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!validateStep()) return;

    if (!isLastStep) {
      setStepIndex((index) => index + 1);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await completeInitialChecklist(buildInitialChecklistPayload(answers));
      if (response.success) {
        // Limpa cache do PatientOnboardingGate para que ele não redirecione novamente
        if (user?.id) {
          localStorage.setItem(`checklist_status_${user.id}`, 'false');
        }
        // Idempotência: marca flag do modal legado (desativado) para não reaparecer se reativado
        const patientId = response.data?.patient?.id;
        if (patientId) {
          localStorage.setItem(`onboarding_${patientId}`, 'true');
        }
        toast.success('Checklist concluido. Prontuario inicial atualizado.');
        navigate('/dashboard', { replace: true });
        return;
      }

      toast.error(response.error || 'Não foi possível concluir o checklist. Verifique os dados informados.');
    } catch (error: any) {
      console.error('Erro ao salvar checklist:', error);
      let message = 'Não foi possível concluir o checklist. Verifique os dados informados e tente novamente.';
      
      // Se for um erro da API do backend com uma mensagem amigável ou array de mensagens
      if (error?.response?.data?.message) {
         const apiMsg = error.response.data.message;
         message = Array.isArray(apiMsg) ? apiMsg.join(', ') : apiMsg;
      } else if (error instanceof Error) {
         // Alguns erros vêm como vírgula-separados ou strings diretas
         message = Array.isArray(error.message) 
           ? (error.message as unknown as string[]).join(', ') 
           : error.message;
      }

      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <main className="mx-auto flex min-h-screen w-full max-w-4xl flex-col px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="mb-3 flex items-center justify-between gap-4">
            <span className="text-sm font-semibold text-teal-700 dark:text-teal-400">
              Etapa {stepIndex + 1} de {initialPatientChecklistSteps.length}
            </span>
            <span className="text-sm font-semibold text-gray-500">{progress}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-neutral-800">
            <div
              className="h-full rounded-full bg-teal-600 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-900 sm:p-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Checklist inicial do paciente
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {currentStep.title}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {currentStep.id === 'personal' && (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Field label={fieldLabels.phone} error={fieldErrors.phone} required>
                  <input
                    value={answers.phone}
                    onChange={(event) => updateAnswer('phone', maskPhone(event.target.value))}
                    className="field-input"
                    placeholder="(11) 99999-9999"
                    inputMode="numeric"
                    maxLength={15}
                  />
                </Field>
                <Field label={fieldLabels.cpf} error={fieldErrors.cpf} required>
                  <input
                    value={answers.cpf}
                    onChange={(event) => updateAnswer('cpf', maskCPF(event.target.value))}
                    className="field-input"
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    maxLength={14}
                  />
                </Field>
                <Field label={fieldLabels.dateOfBirth} error={fieldErrors.dateOfBirth} required>
                  <input
                    type="date"
                    value={answers.dateOfBirth}
                    max={todayISO}
                    onChange={(event) => updateAnswer('dateOfBirth', event.target.value)}
                    className="field-input"
                  />
                </Field>
                <Field label={fieldLabels.gender} error={fieldErrors.gender} required>
                  <select
                    value={answers.gender}
                    onChange={(event) => updateAnswer('gender', event.target.value)}
                    className="field-input"
                  >
                    <option value="">Selecione</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                    <option value="Outro">Prefiro não informar</option>
                  </select>
                </Field>
                <Field label={fieldLabels.isBrazilian} error={fieldErrors.isBrazilian} required>
                  <div className="flex gap-4 items-center h-full pt-2">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="radio"
                        name="isBrazilian"
                        checked={answers.isBrazilian === true}
                        onChange={() => {
                          updateAnswer('isBrazilian', true as any);
                          updateAnswer('country', 'Brasil');
                        }}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 bg-white dark:bg-neutral-900"
                      />
                      Sim
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="radio"
                        name="isBrazilian"
                        checked={answers.isBrazilian === false}
                        onChange={() => {
                          updateAnswer('isBrazilian', false as any);
                          updateAnswer('country', '');
                        }}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 bg-white dark:bg-neutral-900"
                      />
                      Não
                    </label>
                  </div>
                </Field>
                {answers.isBrazilian === false && (
                  <Field label={fieldLabels.country} error={fieldErrors.country} required>
                    <input
                      value={answers.country}
                      onChange={(event) => updateAnswer('country', event.target.value)}
                      className="field-input"
                      placeholder="Ex: Argentina"
                    />
                  </Field>
                )}
              </div>
            )}

            {currentStep.id === 'address' && (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label={fieldLabels.zipCode} error={fieldErrors.zipCode} required>
                    <input
                      value={answers.zipCode}
                      onChange={(event) => handleCepChange(event.target.value)}
                      className="field-input"
                      placeholder="00000-000"
                      inputMode="numeric"
                      maxLength={9}
                    />
                  </Field>
                </div>
                <Field label={fieldLabels.street} error={fieldErrors.street} required>
                  <input
                    value={answers.street}
                    onChange={(event) => updateAnswer('street', event.target.value)}
                    className="field-input"
                  />
                </Field>
                <Field label={fieldLabels.number} error={fieldErrors.number} required>
                  <input
                    ref={numberInputRef}
                    value={answers.number}
                    onChange={(event) => updateAnswer('number', event.target.value.replace(/\D/g, ''))}
                    className="field-input"
                    placeholder="123"
                    inputMode="numeric"
                  />
                </Field>
                <Field label={fieldLabels.complement} error={fieldErrors.complement}>
                  <input
                    value={answers.complement}
                    onChange={(event) => updateAnswer('complement', event.target.value)}
                    className="field-input"
                  />
                </Field>
                <Field label={fieldLabels.neighborhood} error={fieldErrors.neighborhood} required>
                  <input
                    value={answers.neighborhood}
                    onChange={(event) => updateAnswer('neighborhood', event.target.value)}
                    className="field-input"
                  />
                </Field>
                <Field label={fieldLabels.city} error={fieldErrors.city} required>
                  <input
                    value={answers.city}
                    onChange={(event) => updateAnswer('city', event.target.value)}
                    className="field-input"
                  />
                </Field>
                <Field label={fieldLabels.state} error={fieldErrors.state} required>
                  <input
                    value={answers.state}
                    maxLength={2}
                    onChange={(event) => updateAnswer('state', event.target.value.toUpperCase())}
                    className="field-input uppercase"
                  />
                </Field>
              </div>
            )}

            {currentStep.id === 'clinical' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <Field label={fieldLabels.bloodType} error={fieldErrors.bloodType}>
                    <select
                      value={answers.bloodType}
                      onChange={(event) => updateAnswer('bloodType', event.target.value)}
                      className="field-input bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700"
                    >
                      <option value="">Selecione</option>
                      {BLOOD_TYPE_SELECT_OPTIONS.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label={fieldLabels.height} error={fieldErrors.height} required>
                    <input
                      value={answers.height}
                      onChange={(event) => updateAnswer('height', event.target.value)}
                      className="field-input"
                      placeholder="Ex: 1.75"
                      type="number"
                      step="0.01"
                    />
                  </Field>
                  <Field label={fieldLabels.weight} error={fieldErrors.weight} required>
                    <input
                      value={answers.weight}
                      onChange={(event) => updateAnswer('weight', event.target.value)}
                      className="field-input"
                      placeholder="Ex: 70"
                      type="number"
                      step="0.1"
                    />
                  </Field>
                </div>
                <TextField
                  label={fieldLabels.allergies}
                  value={answers.allergies}
                  error={fieldErrors.allergies}
                  onChange={(value) => updateAnswer('allergies', value)}
                  required
                />
                <TextField
                  label={fieldLabels.medications}
                  value={answers.medications}
                  error={fieldErrors.medications}
                  onChange={(value) => updateAnswer('medications', value)}
                  required
                />
                <TextField
                  label={fieldLabels.preExistingConditions}
                  value={answers.preExistingConditions}
                  error={fieldErrors.preExistingConditions}
                  onChange={(value) => updateAnswer('preExistingConditions', value)}
                  required
                />
              </div>
            )}

            {currentStep.id === 'background' && (
              <div className="space-y-5">
                <FamilyDiseaseHistoryFields
                  value={answers.familyHistory}
                  error={fieldErrors.familyHistory}
                  onChange={(next: FamilyDiseaseHistory) => {
                    setAnswers((prev) => ({ ...prev, familyHistory: next }));
                    setFieldErrors((prev) => ({ ...prev, familyHistory: undefined }));
                  }}
                />
                <TextField
                  label={fieldLabels.previousSurgeries}
                  value={answers.previousSurgeries}
                  error={fieldErrors.previousSurgeries}
                  onChange={(value) => updateAnswer('previousSurgeries', value)}
                  required
                />
                <TextField
                  label={fieldLabels.lifestyleHabits}
                  value={answers.lifestyleHabits}
                  error={fieldErrors.lifestyleHabits}
                  onChange={(value) => updateAnswer('lifestyleHabits', value)}
                  required
                />
              </div>
            )}

            <div className="flex items-center justify-between border-t border-gray-100 pt-6 dark:border-neutral-800">
              <button
                type="button"
                onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
                disabled={stepIndex === 0 || isSubmitting}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-700 dark:text-gray-200 dark:hover:bg-neutral-800"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isLastStep ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {isLastStep ? 'Concluir' : 'Continuar'}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

function Field({
  label,
  error,
  required,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-2 text-sm font-medium text-gray-700 dark:text-gray-300">
      <span>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
      {error && <span className="block text-xs text-red-600">{error}</span>}
    </label>
  );
}

function TextField({
  label,
  value,
  error,
  required,
  onChange,
}: {
  label: string;
  value: string;
  error?: string;
  required?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <ConditionalTextarea
      label={label}
      value={value}
      error={error}
      required={required}
      onChange={onChange}
    />
  );
}
